import { FileText, Pencil, Truck, User, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  COMMANDE_STATUT_LABELS,
  COTES_MONTAGE,
  MATIERE_MONTURE_LABELS,
  montageVide,
  type Commande,
} from '@sirajoptic/shared';
import { BadgeAVerifier, BadgeStatutCommande } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, Drawer } from '@/components/ui/dialog';
import { ChargementBloc, Encart, EtatErreur } from '@/components/ui/feedback';
import { useToast } from '@/components/ui/toast';
import { statutAmo } from '@sirajoptic/shared';
import { aujourdhui, date, dh, ordonnance, telephone } from '@/lib/format';
import { useChangerStatut, useCommande, useGenererFacture } from '../api/commandes.api';
import { DeductionStock } from './DeductionStock';
import { PaiementsPanel } from './PaiementsPanel';

/** Détail d'une commande : paiements, stock rattaché, statut, facture. */
export function CommandeDrawer({
  id,
  onFermer,
  onModifier,
}: {
  id: string | null;
  onFermer: () => void;
  onModifier: (commande: Commande) => void;
}) {
  const { data, isPending, error, refetch } = useCommande(id);
  const changerStatut = useChangerStatut();
  const genererFacture = useGenererFacture();
  const [aAnnuler, setAAnnuler] = useState(false);
  const toast = useToast();
  const naviguer = useNavigate();

  if (!id) return null;

  async function livrer() {
    if (!data) return;
    try {
      await changerStatut.mutateAsync({ id: data.id, statut: 'LIVREE' });
      toast.succes('Commande marquée comme livrée.');
    } catch (e) {
      toast.erreur(e instanceof Error ? e.message : 'Changement de statut impossible.');
    }
  }

  async function annuler() {
    if (!data) return;
    try {
      await changerStatut.mutateAsync({ id: data.id, statut: 'ANNULEE' });
      toast.succes('Commande annulée. Le stock rattaché a été restitué.');
      setAAnnuler(false);
    } catch (e) {
      toast.erreur(e instanceof Error ? e.message : 'Annulation impossible.');
    }
  }

  async function facturer() {
    if (!data) return;
    try {
      const facture = await genererFacture.mutateAsync(data.id);
      toast.succes(`Brouillon ${facture.reference} créé.`);
      naviguer(`/factures/${facture.id}`);
    } catch (e) {
      toast.erreur(e instanceof Error ? e.message : 'Génération impossible.');
    }
  }

  const annulee = data?.statut === 'ANNULEE';
  const motifs = Array.isArray(data?.import_raw?._motifs)
    ? (data.import_raw._motifs as string[])
    : undefined;

  return (
    <>
      <Drawer
        ouvert
        onFermer={onFermer}
        titre={data ? data.client_nom : 'Commande'}
        description={
          data
            ? `${date(data.date_commande)}${data.numero_bon ? ` · Bon ${data.numero_bon}` : ''}`
            : undefined
        }
        pied={
          data ? (
            <>
              <Button
                variant="outline"
                icone={<Pencil aria-hidden className="h-4 w-4" />}
                onClick={() => onModifier(data)}
              >
                Modifier
              </Button>
              {data.statut === 'EN_COURS' ? (
                <Button
                  variant="outline"
                  icone={<Truck aria-hidden className="h-4 w-4" />}
                  onClick={() => void livrer()}
                  chargement={changerStatut.isPending}
                >
                  Marquer livrée
                </Button>
              ) : null}
              {!annulee ? (
                <>
                  <Button
                    variant="outline"
                    icone={<XCircle aria-hidden className="h-4 w-4" />}
                    onClick={() => setAAnnuler(true)}
                  >
                    Annuler
                  </Button>
                  <Button
                    variant="primary"
                    icone={<FileText aria-hidden className="h-4 w-4" />}
                    onClick={() => void facturer()}
                    chargement={genererFacture.isPending}
                    disabled={Boolean(data.facture_id)}
                    title={data.facture_id ? 'Une facture existe déjà' : undefined}
                  >
                    Générer la facture
                  </Button>
                </>
              ) : null}
            </>
          ) : null
        }
      >
        {isPending ? (
          <ChargementBloc />
        ) : error ? (
          <EtatErreur erreur={error} onReessayer={() => void refetch()} />
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <BadgeStatutCommande statut={data.statut} />
              {data.a_verifier ? <BadgeAVerifier motifs={motifs} /> : null}
              {data.source === 'IMPORT' ? (
                <span className="text-xs text-ink-muted">Importée depuis Excel</span>
              ) : null}
            </div>

            {data.a_verifier ? (
              <Encart ton="moutarde" titre="Cette ligne demande une vérification">
                {motifs?.length ? (
                  <ul className="ml-4 list-disc">
                    {motifs.map((m) => (
                      <li key={m}>{m}</li>
                    ))}
                  </ul>
                ) : (
                  'Un champ importé n’a pas pu être interprété.'
                )}
              </Encart>
            ) : null}

            {annulee ? (
              <Encart ton="danger">
                Commande annulée — elle n'entre plus dans le chiffre d'affaires ni dans les restes
                dus. Elle reste modifiable et consultable.
              </Encart>
            ) : null}

            {/* Client */}
            <section className="rounded-md border border-line p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">{data.client_nom}</p>
                  <p className="text-xs text-ink-muted">{telephone(data.client_tel)}</p>
                </div>
                {data.client_id ? (
                  <Link
                    to={`/clients/${data.client_id}`}
                    className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-mustard-600 hover:underline"
                  >
                    <User aria-hidden className="h-3.5 w-3.5" />
                    Voir la fiche
                  </Link>
                ) : null}
              </div>
            </section>

            {/* Ordonnance */}
            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink">Ordonnance</h3>
              <div className="grid grid-cols-3 gap-2 rounded-md border border-line p-3 text-center">
                <div>
                  <p className="text-xs uppercase tracking-wide text-ink-muted">OD</p>
                  <p className="mt-0.5 font-mono text-sm">{ordonnance(data.od)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-ink-muted">OG</p>
                  <p className="mt-0.5 font-mono text-sm">{ordonnance(data.og)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-ink-muted">ADD</p>
                  <p className="mt-0.5 font-mono text-sm">{data.addition ?? '—'}</p>
                </div>
              </div>

              {data.prescripteur || data.date_ordonnance ? (
                <p className="mt-2 text-xs text-ink-muted">
                  {data.prescripteur ? <span>Prescrite par {data.prescripteur}</span> : null}
                  {data.prescripteur && data.date_ordonnance ? ' · ' : null}
                  {data.date_ordonnance ? <span>le {date(data.date_ordonnance)}</span> : null}
                </p>
              ) : null}

              {/* L'échéance AMO de **cet équipement-là**, pas celle du client :
                  une commande plus récente la repousserait, et c'est la fiche
                  client qui fait foi pour ça. La date de commande sert de
                  repère quand l'ordonnance n'a pas été datée. */}
              <EcheanceAmo
                depart={data.date_ordonnance ?? data.date_commande}
                estime={!data.date_ordonnance}
              />
            </section>

            {/* Cotes de montage : silencieuses tant qu'elles ne sont pas prises,
                pour ne pas encombrer une commande de lentilles ou de produit. */}
            {montageVide(data) ? null : (
              <section>
                <h3 className="mb-2 text-sm font-semibold text-ink">Cotes de montage</h3>
                <div className="overflow-hidden rounded-md border border-line">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-line bg-surface-sunken">
                        <th className="px-3 py-1.5 text-left text-xs font-medium uppercase tracking-wide text-ink-muted">
                          Cote
                        </th>
                        <th className="w-20 px-3 py-1.5 text-center text-xs font-medium uppercase tracking-wide text-ink-muted">
                          OD
                        </th>
                        <th className="w-20 px-3 py-1.5 text-center text-xs font-medium uppercase tracking-wide text-ink-muted">
                          OG
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {COTES_MONTAGE.map((cote) => (
                        <tr key={cote.cle} className="border-b border-line last:border-b-0">
                          <td className="px-3 py-1.5 text-ink-soft">{cote.libelle}</td>
                          <td className="px-3 py-1.5 text-center font-mono">
                            {data[`${cote.cle}_od`] ?? '—'}
                          </td>
                          <td className="px-3 py-1.5 text-center font-mono">
                            {data[`${cote.cle}_og`] ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-1 text-xs text-ink-muted">
                  En millimètres.
                  {data.matiere_monture ? (
                    <>
                      {' · Monture '}
                      <span className="font-medium text-ink-soft">
                        {MATIERE_MONTURE_LABELS[data.matiere_monture].toLowerCase()}
                      </span>
                    </>
                  ) : null}
                </p>
              </section>
            )}

            {/* Postes */}
            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink">Postes</h3>
              <ul className="divide-y divide-line rounded-md border border-line">
                <Poste libelle="Verre" valeur={data.type_verre} prix={data.prix_verre} />
                <Poste libelle="Monture" valeur={data.monture} prix={data.prix_monture} />
                <Poste libelle="Lentilles" valeur={data.lentilles} prix={data.prix_lentilles} />
                <li className="flex items-center justify-between gap-3 bg-surface-sunken px-3 py-2">
                  <span className="text-sm font-semibold text-ink">Total</span>
                  <span className="text-sm font-semibold tabular text-mustard-600">
                    {dh(data.total)}
                  </span>
                </li>
              </ul>
              {data.societe ? (
                <p className="mt-1.5 text-xs text-ink-muted">Société : {data.societe}</p>
              ) : null}
            </section>

            <PaiementsPanel
              commandeId={data.id}
              paiements={data.paiements}
              total={data.total}
              avance={data.avance}
              reste={data.reste}
              lectureSeule={annulee}
            />

            <DeductionStock
              commandeId={data.id}
              mouvements={data.mouvements}
              desactive={annulee}
            />
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        ouvert={aAnnuler}
        onFermer={() => setAAnnuler(false)}
        onConfirmer={() => void annuler()}
        titre="Annuler cette commande ?"
        libelleConfirmer="Annuler la commande"
        danger
        chargement={changerStatut.isPending}
        message={
          <>
            <p>
              La commande sortira du chiffre d'affaires et des restes dus. Le stock qui lui est
              rattaché est restitué automatiquement.
            </p>
            <p className="mt-2">
              L'annulation est définitive : une commande annulée ne peut plus repasser en «{' '}
              {COMMANDE_STATUT_LABELS.EN_COURS} ».
            </p>
          </>
        }
      />
    </>
  );
}

function Poste({
  libelle,
  valeur,
  prix,
}: {
  libelle: string;
  valeur: string | null;
  prix: number | null;
}) {
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2">
      <span className="w-20 shrink-0 text-xs uppercase tracking-wide text-ink-muted">
        {libelle}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm text-ink">{valeur ?? '—'}</span>
      <span className="shrink-0 text-sm tabular">{prix === null ? '—' : dh(prix)}</span>
    </li>
  );
}

/**
 * Date à laquelle l'AMO peut reprendre l'équipement de cette commande.
 *
 * Une ligne de rappel, pas une promesse : le magasin ne connaît ni le régime
 * du client ni ses droits ouverts. Elle dit quand le délai est franchi, ce qui
 * est la seule chose que la commande sait.
 */
function EcheanceAmo({ depart, estime }: { depart: string | null; estime: boolean }) {
  const statut = statutAmo(depart, aujourdhui());
  if (!statut) return null;

  return (
    <p className="mt-1 text-xs text-ink-muted">
      Renouvellement AMO {statut.etat === 'RENOUVELABLE' ? 'possible depuis le' : 'à partir du'}{' '}
      <span className="font-medium text-ink-soft">{date(statut.echeance)}</span>
      {estime ? " — calculé sur la date de commande, l'ordonnance n'étant pas datée." : '.'}
    </p>
  );
}
